import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Calendar, MapPin, BarChart3, Edit, Trash2, Eye, EyeOff, TicketIcon, TrendingUp, DollarSign, Users, X, Upload, Wallet, CheckCircle, XCircle, Clock, Mail, MailOpen, Zap } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import { organiserAPI, eventsAPI, payoutAPI, organizersAPI, boostAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import { canCreateEvents } from '../lib/roles';
import { CATEGORIES as EVENT_CATEGORIES, EUROPEAN_COUNTRIES, SUGGESTED_TAGS } from '../lib/eventMeta';

const CATEGORIES = EVENT_CATEGORIES.map(c => c.value);

const DEFAULT_EVENT = {
  title: '', description: '', category: 'sports', date: '', time: '',
  location: '', city: '', country: 'Poland', image_url: '',
  ticket_types: [{ id: 'general', name: 'General Admission', price: 25.0, available: 100 }],
  is_published: false, is_featured: false, tags: []
};

export default function OrganiserDashboard() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [tab, setTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [form, setForm] = useState(DEFAULT_EVENT);
  const [tagInput, setTagInput] = useState('');
  const [payoutForm, setPayoutForm] = useState({ amount: '', currency: 'EUR', bank_account: '', paypal_email: '', note: '' });
  const [payoutSubmitting, setPayoutSubmitting] = useState(false);
  const [showPayoutForm, setShowPayoutForm] = useState(false);
  const [boostEvent, setBoostEvent] = useState(null);
  const [boostPlans, setBoostPlans] = useState([]);
  const [boostDuration, setBoostDuration] = useState(null);
  const [boostSubmitting, setBoostSubmitting] = useState(false);

  useEffect(() => {
    // Organisers, DJs (who run their own nights) and admins.
    if (!authLoading && (!user || !canCreateEvents(user))) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    setLoading(true);
    Promise.all([organiserAPI.events(), organiserAPI.analytics(), payoutAPI.myRequests(), organizersAPI.myMessages()])
      .then(([evtResp, anaResp, payResp, msgResp]) => {
        setEvents(evtResp.data || []);
        setAnalytics(anaResp.data);
        setPayouts(payResp.data.payout_requests || []);
        setMessages(msgResp.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openCreate = () => { setForm(DEFAULT_EVENT); setEditEvent(null); setShowForm(true); };
  const openEdit = (ev) => { setForm({ ...ev }); setEditEvent(ev.event_id); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const { image_url, ticket_types, ...rest } = form;
      const payload = {
        ...rest,
        imageUrl: image_url,
        ticketTypes: ticket_types.map(({ id, ...tt }) => tt),
      };
      if (editEvent) {
        await eventsAPI.update(editEvent, payload);
        toast.success('Event updated!');
      } else {
        await eventsAPI.create(payload);
        toast.success('Event created!');
      }
      setShowForm(false);
      const resp = await organiserAPI.events();
      setEvents(resp.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save event');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await eventsAPI.delete(eventId);
      setEvents(evs => evs.filter(e => e.event_id !== eventId));
      toast.success('Event deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleTogglePublish = async (ev) => {
    try {
      await eventsAPI.update(ev.event_id, { is_published: !ev.is_published });
      setEvents(evs => evs.map(e => e.event_id === ev.event_id ? { ...e, is_published: !e.is_published } : e));
      toast.success(ev.is_published ? 'Event unpublished' : 'Event published!');
    } catch { toast.error('Failed to update'); }
  };

  const addTicketType = () => {
    const id = `tier_${Date.now()}`;
    setForm(f => ({ ...f, ticket_types: [...f.ticket_types, { id, name: '', price: 0, available: 100 }] }));
  };

  const updateTicketType = (idx, field, value) => {
    setForm(f => {
      const tts = [...f.ticket_types];
      tts[idx] = { ...tts[idx], [field]: field === 'price' || field === 'available' ? Number(value) : value };
      return { ...f, ticket_types: tts };
    });
  };

  const removeTicketType = (idx) => {
    setForm(f => ({ ...f, ticket_types: f.ticket_types.filter((_, i) => i !== idx) }));
  };

  const handleMarkRead = async (messageId) => {
    try {
      await organizersAPI.markMessageRead(messageId);
      setMessages(msgs => msgs.map(m => m.message_id === messageId ? { ...m, is_read: true } : m));
    } catch { toast.error('Failed to update message'); }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  const openBoost = async (ev) => {
    setBoostEvent(ev);
    setBoostDuration(null);
    if (boostPlans.length === 0) {
      try {
        const r = await boostAPI.plans();
        setBoostPlans(r.data || []);
      } catch { toast.error('Failed to load boost plans'); }
    }
  };

  const handleBoostCheckout = async () => {
    if (!boostEvent || !boostDuration) return;
    setBoostSubmitting(true);
    try {
      const r = await boostAPI.create({
        eventId: boostEvent.event_id,
        durationDays: boostDuration,
        hostUrl: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start boost checkout');
    } finally {
      setBoostSubmitting(false);
    }
  };

  const STAT_CARDS = analytics ? [
    { label: 'Total Events', value: analytics.total_events, icon: Calendar, bg: 'bg-blue-500' },
    { label: 'Published', value: analytics.published_events, icon: Eye, bg: 'bg-green-500' },
    { label: 'Tickets Sold', value: analytics.total_tickets_sold, icon: TicketIcon, bg: 'bg-purple-500' },
    { label: 'Revenue', value: format(analytics.total_revenue), icon: DollarSign, bg: 'bg-orange-500' },
  ] : [];

  return (
    <div className="min-h-screen bg-[#050505] pt-16 page-enter" data-testid="organiser-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black uppercase tracking-tight text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {t('organiserDashboard')}
            </h1>
            <p className="text-gray-500 text-sm mt-1">Welcome, {user?.name}</p>
          </div>
          <button onClick={openCreate} className="btn-primary px-5 py-2.5 rounded flex items-center gap-2 text-sm font-bold" data-testid="create-event-btn">
            <Plus size={16} /> {t('createEvent')}
          </button>
        </div>

        {/* Stats */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {STAT_CARDS.map(({ label, value, icon: Icon, bg }) => (
              <div key={label} className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-5">
                <div className={`${bg} w-9 h-9 rounded-lg flex items-center justify-center mb-3`}>
                  <Icon size={17} className="text-white" />
                </div>
                <p className="text-white font-black text-2xl">{value}</p>
                <p className="text-gray-500 text-xs uppercase tracking-wider">{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-[#0A0A0A] border border-gray-800 rounded-lg p-1 w-fit">
          {['events', 'analytics', 'payouts', 'messages'].map(t2 => (
            <button
              key={t2}
              onClick={() => setTab(t2)}
              className={`relative px-4 py-2 rounded text-xs font-bold uppercase transition-colors ${tab === t2 ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-white'}`}
              data-testid={`tab-${t2}`}
            >
              {t2 === 'messages' ? 'Messages' : t(t2)}
              {t2 === 'messages' && unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-yellow-500 text-black text-[10px] font-black rounded-full w-4 h-4 flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === 'events' && (
          <div>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-48 rounded-xl" />)}
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No events yet. Create your first event!</p>
                <button onClick={openCreate} className="btn-primary px-5 py-2.5 rounded text-sm font-bold">Create Event</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="events-list">
                {events.map(ev => (
                  <div key={ev.event_id} className="bg-[#0A0A0A] border border-gray-800 rounded-xl overflow-hidden" data-testid={`organiser-event-${ev.event_id}`}>
                    <div className="relative h-32">
                      <img src={ev.image_url || 'https://images.unsplash.com/photo-1765278797923-10a027f5c69d?w=600&q=80'} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute bottom-2 left-3 flex items-center gap-2">
                        <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${ev.is_published ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'}`}>
                          {ev.is_published ? 'Published' : 'Draft'}
                        </span>
                        {ev.is_featured && <span className="gold-badge text-xs px-2 py-0.5 rounded-sm">Featured</span>}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-black uppercase text-sm leading-tight mb-2 truncate" style={{ fontFamily: 'Montserrat, sans-serif' }}>{ev.title}</h3>
                      <div className="flex items-center gap-3 text-gray-500 text-xs mb-3">
                        <span className="flex items-center gap-1"><Calendar size={11} /> {ev.date}</span>
                        <span className="flex items-center gap-1"><MapPin size={11} /> {ev.city}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(ev)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-white px-2 py-1 border border-gray-700 rounded transition-colors" data-testid={`edit-${ev.event_id}`}>
                          <Edit size={11} /> Edit
                        </button>
                        <button onClick={() => handleTogglePublish(ev)} className={`flex items-center gap-1 text-xs px-2 py-1 border rounded transition-colors ${ev.is_published ? 'text-orange-400 border-orange-400/30 hover:bg-orange-400/10' : 'text-green-400 border-green-400/30 hover:bg-green-400/10'}`} data-testid={`publish-${ev.event_id}`}>
                          {ev.is_published ? <><EyeOff size={11} /> Unpublish</> : <><Eye size={11} /> Publish</>}
                        </button>
                        <Link to={`/events/${ev.event_id}`} className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 px-2 py-1 border border-blue-400/30 rounded transition-colors">
                          <Eye size={11} /> View
                        </Link>
                        <Link to={`/team/event/${ev.event_id}`} className="flex items-center gap-1 text-xs text-gray-300 hover:text-white px-2 py-1 border border-gray-700 rounded transition-colors">
                          <Users size={11} /> Team
                        </Link>
                        <Link to={`/check-in/${ev.event_id}`} className="flex items-center gap-1 text-xs text-gray-300 hover:text-white px-2 py-1 border border-gray-700 rounded transition-colors">
                          <TicketIcon size={11} /> Check-in
                        </Link>
                        <button onClick={() => openBoost(ev)} className="flex items-center gap-1 text-xs text-yellow-400 hover:text-yellow-300 px-2 py-1 border border-yellow-400/30 rounded transition-colors" data-testid={`boost-${ev.event_id}`}>
                          <Zap size={11} /> Boost
                        </button>
                        <button onClick={() => handleDelete(ev.event_id)} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 px-2 py-1 border border-red-400/30 rounded transition-colors ml-auto" data-testid={`delete-${ev.event_id}`}>
                          <Trash2 size={11} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'analytics' && analytics && (
          <div className="space-y-6">
            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Total Orders', value: analytics.total_orders, color: 'text-white' },
                { label: 'Revenue', value: format(analytics.total_revenue), color: 'text-yellow-400' },
                { label: 'Tickets Sold', value: analytics.total_tickets_sold, color: 'text-red-400' },
              ].map(({ label, value, color }) => (
                <div key={label} className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-5">
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{label}</p>
                  <p className={`font-black text-3xl ${color}`}>{value}</p>
                </div>
              ))}
            </div>

            {/* Revenue by Event chart */}
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6" data-testid="revenue-by-event-chart">
              <h3 className="text-white font-black uppercase text-sm mb-6 tracking-wider">Revenue by Event</h3>
              {analytics.revenue_by_event?.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(160, analytics.revenue_by_event.length * 44)}>
                  <BarChart
                    data={analytics.revenue_by_event}
                    layout="vertical"
                    margin={{ top: 0, right: 24, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fill: '#666', fontSize: 11 }}
                      tickFormatter={v => `€${v}`}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="title"
                      width={160}
                      tick={{ fill: '#a1a1aa', fontSize: 11 }}
                      tickFormatter={v => v.length > 20 ? v.slice(0, 20) + '…' : v}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                      contentStyle={{ background: '#111', border: '1px solid #262626', borderRadius: 8, fontSize: 12 }}
                      formatter={(value, name, props) => [`€${value.toFixed(2)} · ${props.payload.tickets} tickets`, 'Revenue']}
                      labelStyle={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}
                    />
                    <Bar dataKey="revenue" fill="#E63946" radius={[0, 4, 4, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-600">
                  <BarChart3 size={32} className="mb-2" />
                  <p className="text-sm">No paid orders yet</p>
                </div>
              )}
            </div>

            {/* Daily revenue trend */}
            {analytics.daily_revenue?.length > 1 && (
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6" data-testid="daily-revenue-chart">
                <h3 className="text-white font-black uppercase text-sm mb-6 tracking-wider">Revenue Trend</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={analytics.daily_revenue} margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1f1f1f" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#666', fontSize: 10 }}
                      tickFormatter={v => v.slice(5)}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fill: '#666', fontSize: 10 }}
                      tickFormatter={v => `€${v}`}
                      axisLine={false}
                      tickLine={false}
                      width={50}
                    />
                    <Tooltip
                      contentStyle={{ background: '#111', border: '1px solid #262626', borderRadius: 8, fontSize: 12 }}
                      formatter={v => [`€${v.toFixed(2)}`, 'Revenue']}
                      labelStyle={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}
                    />
                    <Line type="monotone" dataKey="revenue" stroke="#E63946" strokeWidth={2} dot={{ fill: '#E63946', r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Recent Orders table */}
            {analytics.recent_orders?.length > 0 && (
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-6">
                <h3 className="text-white font-black uppercase text-sm mb-4 tracking-wider">Recent Orders</h3>
                <div className="space-y-2">
                  {analytics.recent_orders.map(order => (
                    <div key={order.order_id} className="flex items-center justify-between py-2.5 border-b border-gray-800/60 text-sm">
                      <div>
                        <p className="text-white font-medium truncate max-w-48">{order.event_title}</p>
                        <p className="text-gray-500 text-xs font-mono">{order.order_id?.slice(0,8).toUpperCase()}</p>
                      </div>
                      <span className="text-white font-bold">{format(order.total_amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payouts Tab */}
        {tab === 'payouts' && (
          <div className="space-y-6" data-testid="payouts-tab">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-white font-black uppercase text-lg">Payout Requests</h2>
                <p className="text-gray-500 text-xs mt-0.5">Request withdrawals from your event revenue</p>
              </div>
              <button
                onClick={() => setShowPayoutForm(v => !v)}
                className="btn-primary px-4 py-2 rounded text-xs font-bold flex items-center gap-1.5"
                data-testid="request-payout-btn"
              >
                <Wallet size={14} /> {t('requestPayout')}
              </button>
            </div>

            {/* Payout request form */}
            {showPayoutForm && (
              <div className="bg-[#0A0A0A] border border-gray-700 rounded-xl p-6" data-testid="payout-form">
                <h3 className="text-white font-bold text-sm mb-4">New Payout Request</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-gray-400 text-xs uppercase mb-1.5 block">{t('amount')} *</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        value={payoutForm.amount}
                        onChange={e => setPayoutForm(f => ({ ...f, amount: e.target.value }))}
                        className="flex-1 bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm"
                        placeholder="0.00"
                        data-testid="payout-amount-input"
                      />
                      <select
                        value={payoutForm.currency}
                        onChange={e => setPayoutForm(f => ({ ...f, currency: e.target.value }))}
                        className="bg-[#111] border border-gray-700 text-white px-2 py-2.5 rounded text-sm"
                      >
                        {['EUR', 'PLN', 'GBP', 'USD'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs uppercase mb-1.5 block">{t('bankAccount')}</label>
                    <input
                      value={payoutForm.bank_account}
                      onChange={e => setPayoutForm(f => ({ ...f, bank_account: e.target.value }))}
                      className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm"
                      placeholder="IBAN or account number"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs uppercase mb-1.5 block">{t('paypalEmail')}</label>
                    <input
                      type="email"
                      value={payoutForm.paypal_email}
                      onChange={e => setPayoutForm(f => ({ ...f, paypal_email: e.target.value }))}
                      className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm"
                      placeholder="paypal@email.com"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs uppercase mb-1.5 block">{t('note')}</label>
                    <input
                      value={payoutForm.note}
                      onChange={e => setPayoutForm(f => ({ ...f, note: e.target.value }))}
                      className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm"
                      placeholder="Optional note"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => setShowPayoutForm(false)} className="flex-1 py-2.5 border border-gray-700 rounded text-sm text-gray-400 hover:text-white transition-colors">{t('cancel')}</button>
                  <button
                    onClick={async () => {
                      if (!payoutForm.amount || parseFloat(payoutForm.amount) <= 0) { toast.error('Enter a valid amount'); return; }
                      if (!payoutForm.bank_account && !payoutForm.paypal_email) { toast.error('Provide bank account or PayPal email'); return; }
                      setPayoutSubmitting(true);
                      try {
                        const r = await payoutAPI.create({ ...payoutForm, amount: parseFloat(payoutForm.amount) });
                        setPayouts(p => [r.data, ...p]);
                        setShowPayoutForm(false);
                        setPayoutForm({ amount: '', currency: 'EUR', bank_account: '', paypal_email: '', note: '' });
                        toast.success('Payout request submitted!');
                      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                      finally { setPayoutSubmitting(false); }
                    }}
                    disabled={payoutSubmitting}
                    className="flex-1 btn-primary py-2.5 rounded text-sm font-bold disabled:opacity-40"
                    data-testid="submit-payout-btn"
                  >
                    Submit Request
                  </button>
                </div>
              </div>
            )}

            {/* Payout requests list */}
            {payouts.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-800 rounded-xl">
                <Wallet size={32} className="text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No payout requests yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {payouts.map(req => (
                  <div key={req.payout_id} className="bg-[#0A0A0A] border border-gray-800 rounded-xl p-5 flex items-center justify-between gap-4" data-testid={`payout-${req.payout_id}`}>
                    <div>
                      <p className="text-white font-bold text-lg">{format(req.amount)}</p>
                      <p className="text-gray-500 text-xs font-mono">{req.payout_id?.slice(0, 12).toUpperCase()}</p>
                      {req.note && <p className="text-gray-400 text-xs mt-1">{req.note}</p>}
                    </div>
                    <div className="text-right">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                        req.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                        req.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                        'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {req.status === 'pending' ? <span className="flex items-center gap-1"><Clock size={10}/>{t('pending')}</span> :
                         req.status === 'approved' ? <span className="flex items-center gap-1"><CheckCircle size={10}/>{t('approved')}</span> :
                         <span className="flex items-center gap-1"><XCircle size={10}/>{t('rejected')}</span>}
                      </span>
                      <p className="text-gray-600 text-xs mt-1">{req.created_at?.slice(0, 10)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Messages Tab */}
        {tab === 'messages' && (
          <div className="space-y-3" data-testid="messages-tab">
            {messages.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-gray-800 rounded-xl">
                <Mail size={32} className="text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">No messages yet</p>
              </div>
            ) : (
              messages.map(msg => (
                <div
                  key={msg.message_id}
                  className={`bg-[#0A0A0A] border rounded-xl p-5 ${msg.is_read ? 'border-gray-800' : 'border-red-600/40'}`}
                  data-testid={`message-${msg.message_id}`}
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex items-center gap-2">
                      {msg.is_read ? <MailOpen size={14} className="text-gray-600" /> : <Mail size={14} className="text-red-400" />}
                      <p className="text-white font-semibold text-sm">{msg.sender_name}</p>
                      <span className="text-gray-500 text-xs">{msg.sender_email}</span>
                    </div>
                    <span className="text-gray-600 text-xs flex-shrink-0">{msg.created_at?.slice(0, 10)}</span>
                  </div>
                  <p className="text-gray-300 text-sm mb-3">{msg.message}</p>
                  <div className="flex items-center gap-3">
                    <a href={`mailto:${msg.sender_email}`} className="text-xs text-blue-400 hover:text-blue-300">Reply by email</a>
                    {!msg.is_read && (
                      <button onClick={() => handleMarkRead(msg.message_id)} className="text-xs text-gray-400 hover:text-white">
                        Mark as read
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Event Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 overflow-y-auto" data-testid="event-form-modal">
          <div className="min-h-screen flex items-start justify-center p-4 py-8">
            <div className="bg-[#0A0A0A] border border-gray-700 rounded-xl w-full max-w-2xl">
              <div className="flex items-center justify-between p-6 border-b border-gray-800">
                <h2 className="text-white font-black uppercase text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  {editEvent ? 'Edit Event' : 'Create New Event'}
                </h2>
                <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">Event Title *</label>
                    <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" data-testid="event-title-input" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">Description *</label>
                    <textarea required value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" data-testid="event-desc-input" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">Category</label>
                    <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" style={{ background: '#111' }}>
                      {CATEGORIES.map(c => <option key={c} value={c} style={{ background: '#111' }}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">Image URL</label>
                    <input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" placeholder="https://..." />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">Date *</label>
                    <input required type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" data-testid="event-date-input" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">Time</label>
                    <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">Location/Venue *</label>
                    <input required value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" data-testid="event-location-input" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">City *</label>
                    <input required value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" data-testid="event-city-input" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">Country</label>
                    <select value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} className="w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm" style={{ background: '#111' }}>
                      {EUROPEAN_COUNTRIES.map(c => <option key={c} value={c} style={{ background: '#111' }}>{c}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1.5 block">Hashtags</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {SUGGESTED_TAGS.map(tag => {
                        const active = form.tags.includes(tag);
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => setForm(f => ({
                              ...f,
                              tags: active ? f.tags.filter(t => t !== tag) : [...f.tags, tag],
                            }))}
                            className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                              active ? 'bg-red-600 border-red-600 text-white' : 'border-gray-700 text-gray-400 hover:border-gray-500'
                            }`}
                          >
                            #{tag}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex gap-2">
                      <input
                        value={tagInput}
                        onChange={e => setTagInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const tag = tagInput.trim().toLowerCase();
                            if (tag && !form.tags.includes(tag)) setForm(f => ({ ...f, tags: [...f.tags, tag] }));
                            setTagInput('');
                          }
                        }}
                        placeholder="Add a custom tag and press Enter"
                        className="flex-1 bg-[#111] border border-gray-700 text-white px-3 py-2 rounded text-xs"
                      />
                    </div>
                    {form.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {form.tags.map(tag => (
                          <span key={tag} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-red-600/20 text-red-400 border border-red-600/30">
                            #{tag}
                            <button type="button" onClick={() => setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }))}>
                              <X size={10} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Ticket Types */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider">Ticket Types</label>
                    <button type="button" onClick={addTicketType} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                      <Plus size={12} /> Add Tier
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.ticket_types.map((tt, idx) => (
                      <div key={idx} className="grid grid-cols-4 gap-2 items-center">
                        <input value={tt.name} onChange={e => updateTicketType(idx, 'name', e.target.value)} placeholder="Ticket name" className="col-span-2 bg-[#111] border border-gray-700 text-white px-2 py-2 rounded text-xs" />
                        <input type="number" value={tt.price} onChange={e => updateTicketType(idx, 'price', e.target.value)} placeholder="Price €" className="bg-[#111] border border-gray-700 text-white px-2 py-2 rounded text-xs" step="0.01" min="0" />
                        <div className="flex items-center gap-1">
                          <input type="number" value={tt.available} onChange={e => updateTicketType(idx, 'available', e.target.value)} placeholder="Qty" className="flex-1 bg-[#111] border border-gray-700 text-white px-2 py-2 rounded text-xs" min="1" />
                          {idx > 0 && <button type="button" onClick={() => removeTicketType(idx)} className="text-red-400 hover:text-red-300"><X size={14} /></button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_published} onChange={e => setForm(f => ({ ...f, is_published: e.target.checked }))} className="w-4 h-4 accent-red-500" data-testid="publish-checkbox" />
                    <span className="text-gray-400 text-xs uppercase font-medium">Publish immediately</span>
                  </label>
                  <span className="text-gray-600 text-xs">Want top placement? Use the <Zap size={11} className="inline -mt-0.5 text-yellow-400" /> Boost button after saving.</span>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-gray-700 rounded text-sm text-gray-400 hover:text-white font-bold transition-colors">Cancel</button>
                  <button type="submit" className="flex-1 btn-primary py-2.5 rounded text-sm font-bold" data-testid="save-event-btn">
                    {editEvent ? 'Update Event' : 'Create Event'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Boost Modal */}
      {boostEvent && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="boost-modal">
          <div className="bg-[#0A0A0A] border border-gray-700 rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <h2 className="text-white font-black uppercase text-lg flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <Zap size={18} className="text-yellow-400" /> Boost Visibility
              </h2>
              <button onClick={() => setBoostEvent(null)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-gray-400 text-sm">
                Boost <span className="text-white font-semibold">{boostEvent.title}</span> to the top of Explore for a set number of days. Payment is processed securely by Stripe.
              </p>
              <div className="space-y-2">
                {boostPlans.length === 0 ? (
                  <div className="skeleton h-14 rounded-lg" />
                ) : (
                  boostPlans.map(plan => (
                    <button
                      key={plan.duration_days}
                      onClick={() => setBoostDuration(plan.duration_days)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border text-left transition-colors ${
                        boostDuration === plan.duration_days ? 'border-yellow-400 bg-yellow-400/10' : 'border-gray-700 hover:border-gray-500'
                      }`}
                      data-testid={`boost-plan-${plan.duration_days}`}
                    >
                      <span className="text-white text-sm font-bold">{plan.duration_days} days</span>
                      <span className="text-yellow-400 font-black">{format(plan.amount)}</span>
                    </button>
                  ))
                )}
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setBoostEvent(null)} className="flex-1 py-2.5 border border-gray-700 rounded text-sm text-gray-400 hover:text-white font-bold transition-colors">Cancel</button>
                <button
                  onClick={handleBoostCheckout}
                  disabled={!boostDuration || boostSubmitting}
                  className="flex-1 btn-primary py-2.5 rounded text-sm font-bold disabled:opacity-40"
                  data-testid="boost-checkout-btn"
                >
                  {boostSubmitting ? 'Redirecting…' : 'Continue to Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
